import { loadEncounters, loadUnits } from "../../data/loadEncounters";
import { getSystem, loadSystems } from "../../data/loadSystems";
import {
  assembleCombatBoard,
  assembleHaulingBoard,
  assembleMiningBoard,
  assembleVipBoard
} from "../../missionBoardAssembly";
import {
  combineMissionBoard,
  filterMissionsByType,
  tagCombatMissions,
  tagHaulingMissions,
  tagMiningMissions,
  tagVipTransportMissions,
  type BoardMission,
  type MissionType
} from "../../missionBoard";
import { effectivePlayerDps } from "../../droneSystem";
import type { MiningHullTier } from "../../miningMissions";
import { timeToMineSeconds } from "../../miningYield";
import { routeParTimeSeconds } from "../../shipSpeed";
import { shortestRoute } from "../../haulingMissions";
import { createMissionId } from "../../acceptedMissions";
import { effectiveNow } from "../../gameClock";
import { posterFactionForSystem, standingTier } from "../../factionStanding";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { bandClass, formatPercent, formatSeconds, renderHeader } from "../components";

const encounters = loadEncounters();
const units = loadUnits();
const systems = loadSystems();

const FILTERS: (MissionType | "All")[] = ["All", "Combat", "Mining", "Hauling", "VIP Transport"];

function generateBoard(): BoardMission[] {
  const ship = state.currentShip;
  if (!ship) {
    return [];
  }
  const currentSystem = getSystem(state.currentSystemId)!;

  // Loaner Ship: Faction Missions only (16.63) -- a board whose poster
  // isn't one of the three real factions (safe/neutral space, or a
  // Pirate-region board) has nothing a loaner captain is allowed to
  // accept at all.
  if (state.isLoanerShip) {
    const poster = posterFactionForSystem(currentSystem);
    if (!poster || poster === "Pirate") {
      return [];
    }
  }
  // 16.32 (clarified): player DPS is weapon DPS + Drone Bay DPS combined
  // when a bay is fitted -- a Miner/Hauler's 0 weapon DPS is not "0 DPS"
  // for the Mission Difficulty Score engine.
  const dps = effectivePlayerDps(ship.dps, ship.hullTier, ship.role);
  const player = { ehp: ship.hullHp + ship.shieldHp, dps };

  // Pirate-Friendly standing unlocks pirate-exclusive Mission Board
  // contracts (16.55) -- a dedicated pool only offered to Pirate-Friendly
  // captains, parallel to how real-faction Friendly standing already
  // unlocks higher-tier missions (16.48). Concretely: any candidate
  // targeting a pirate-region system is filtered out of the board unless
  // Pirate standing is Friendly.
  const pirateFriendly = standingTier(state.standing.Pirate) === "Friendly";
  const isPirateSystem = (systemId: string): boolean => getSystem(systemId)?.region === "pirate";
  const pirateGate = (systemId: string): boolean => pirateFriendly || !isPirateSystem(systemId);

  const combat = tagCombatMissions(
    assembleCombatBoard(player, ship.hullTier, currentSystem, systems, encounters, units).filter((a) =>
      pirateGate(a.systemId)
    )
  );

  // Mining missions don't exist for T3/T4 ships (CLAUDE.md, 16.24) --
  // simply omitted rather than erroring.
  const mining =
    ship.hullTier === "T1" || ship.hullTier === "T2"
      ? tagMiningMissions(
          assembleMiningBoard(
            player,
            ship.cargoM3,
            ship.hullTier as MiningHullTier,
            currentSystem,
            systems,
            encounters,
            units
          ).filter((a) => pirateGate(a.systemId))
        )
      : [];

  const hauling = tagHaulingMissions(
    assembleHaulingBoard(currentSystem, ship.hullTier, systems).filter(
      (h) => pirateGate(h.originSystemId) && pirateGate(h.destinationSystemId)
    )
  );
  const vip = tagVipTransportMissions(
    assembleVipBoard(currentSystem, ship.hullTier, ship.cargoM3, systems).filter(
      (v) => pirateGate(v.originSystemId) && pirateGate(v.destinationSystemId)
    )
  );

  return combineMissionBoard(combat, mining, hauling, vip);
}

// Live hop-distance from wherever the player actually is right now -- never
// stored, always recomputed (16.34), and distinct from a Hauling/VIP
// mission's own origin->destination hop count (16.26 allows a remote
// origin, so "how far is this from me" and "how long is the route itself"
// can genuinely differ).
function hopsFromCurrent(targetSystemId: string): number {
  if (targetSystemId === state.currentSystemId) {
    return 0;
  }
  return shortestRoute(state.currentSystemId, targetSystemId, systems).hops;
}

export function renderMissionBoardScreen(container: HTMLElement, ctx: ScreenContext): void {
  if (!state.currentShip) {
    ctx.navigate("ships");
    return;
  }
  const ship = state.currentShip;

  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  const board = generateBoard();
  const visible = state.boardFilter === "All" ? board : filterMissionsByType(board, state.boardFilter);

  wrap.innerHTML = `
    <h1>Mission Board</h1>
    <p class="dim">
      Regenerated live for ${ship.name} at ${getSystem(state.currentSystemId)!.name} (16.33) --
      ${board.length} missions across all four types.
    </p>

    <div class="filter-row">
      ${FILTERS.map(
        (f) =>
          `<button type="button" class="pill-btn ${state.boardFilter === f ? "active" : ""}" data-filter="${f}">${f}</button>`
      ).join("")}
    </div>

    <div class="mission-list">
      ${visible.length === 0 ? '<p class="dim">No missions of this type right now.</p>' : visible.map((m, i) => missionCardHtml(m, i)).join("")}
    </div>
  `;

  wrap.querySelectorAll<HTMLButtonElement>("button[data-filter]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.boardFilter = btn.dataset.filter as MissionType | "All";
      ctx.rerender();
    });
  });

  wrap.querySelectorAll<HTMLButtonElement>("button[data-accept]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const mission = visible[Number(btn.dataset.accept)];
      acceptMission(mission);
      triggerSave(ctx); // mission accepted is a key state change (16.34-adjacent save policy)
      ctx.navigate("diary");
    });
  });

  container.appendChild(wrap);
}

function missionCardHtml(m: BoardMission, index: number): string {
  const system = getSystem(m.systemId);
  const systemLabel = system ? `${system.name} (Tier ${system.dangerTier})` : m.systemId;

  if (m.type === "Combat") {
    const c = m.combat;
    const hops = hopsFromCurrent(m.systemId);
    return `
      <div class="mission-card">
        <div class="mission-card-title">
          <span class="pill ${bandClass(c.difficulty.band)}">${c.difficulty.band}</span>
          <strong>${c.encounter.name}</strong>
          <span class="dim">@ ${systemLabel} -- ${hops} hops from here</span>
        </div>
        <div class="dim">
          ${c.encounter.faction}, ${c.encounter.structure} -- TTK ${formatSeconds(c.difficulty.ttkSeconds)},
          ${formatPercent(c.difficulty.damageTakenPercent)}% damage taken -- ${c.rewardCr} CR
        </div>
        <p class="note">Accepting ties this specific encounter to a mission. Resolving it (Engage, in the
        Mission Diary) runs the same locked math (16.29/16.30/16.32) instantly to a win/lose outcome --
        the paced/animated presentation is still deliberately deferred.</p>
        <button type="button" data-accept="${index}">Accept</button>
      </div>
    `;
  }

  if (m.type === "Mining") {
    const mm = m.mining;
    const hullTier = state.currentShip!.hullTier as MiningHullTier;
    const seconds = timeToMineSeconds(mm.quotaM3, hullTier);
    const hops = hopsFromCurrent(m.systemId);
    // "Solo it" vs "flee" (16.23/16.24): validated against 16.23's own
    // drone-vs-foe table as damageTakenPercent < 100% -- the ship survives
    // long enough to finish the threat off, using the same drone-bay-
    // inclusive player stats as the Combat board.
    const threatLabel = mm.threat
      ? `-- threat: ${mm.threat.name} ${
          mm.threatDifficulty!.damageTakenPercent < 100
            ? '<span class="pill tag-solo">solo it</span>'
            : '<span class="pill tag-flee">flee</span>'
        }`
      : "-- no threat";
    return `
      <div class="mission-card">
        <div class="mission-card-title">
          <span class="pill ${bandClass(mm.band)}">${mm.band}${mm.threatKind ? "/" + mm.threatKind : ""}</span>
          <strong>${mm.quotaM3} m3 ${mm.resource}</strong>
          <span class="dim">@ ${systemLabel} -- ${hops} hops from here</span>
        </div>
        <div class="dim">
          ${mm.richness} node ${threatLabel} --
          ~${formatSeconds(seconds)} to mine -- ${mm.rewardCr} CR
        </div>
        <button type="button" data-accept="${index}">Accept</button>
      </div>
    `;
  }

  if (m.type === "Hauling") {
    const h = m.hauling;
    const destination = getSystem(h.destinationSystemId);
    const seconds = routeParTimeSeconds(h.hops, 1.0, 1.0); // neutral placeholder speed, see note below
    const hops = hopsFromCurrent(h.destinationSystemId);
    return `
      <div class="mission-card">
        <div class="mission-card-title">
          <strong>Haul to ${destination?.name ?? h.destinationSystemId}</strong>
          <span class="dim">${h.hops}-hop route, max Danger Tier ${h.maxDangerTier} -- ${hops} hops from here</span>
        </div>
        <div class="dim">~${formatSeconds(seconds)} travel time -- ${h.rewardCr} CR</div>
        <p class="note dim">Travel time uses a neutral 1.0x speed baseline -- per-ship speed multipliers
        aren't locked in the design doc yet (16.28).</p>
        <button type="button" data-accept="${index}">Accept</button>
      </div>
    `;
  }

  // VIP Transport
  const v = m.vip;
  const destination = getSystem(v.destinationSystemId);
  const hops = hopsFromCurrent(v.destinationSystemId);
  return `
    <div class="mission-card">
      <div class="mission-card-title">
        <strong>VIP to ${destination?.name ?? v.destinationSystemId}</strong>
        <span class="dim">${v.hops}-hop route, ${v.vipCount} VIPs (${v.cargoUsedM3} m3) -- ${hops} hops from here</span>
      </div>
      <div class="dim">${v.rewardCr} CR</div>
      <p class="note dim">Has a hard deadline once accepted (16.34) -- 1.5x this ship's expected travel
      time. Cannot be abandoned; the VIPs disembark on their own if the deadline passes first.</p>
      <button type="button" data-accept="${index}">Accept</button>
    </div>
  `;
}

// A Loaner Ship pays 30% of the normal reward (16.63) -- a real, steep
// discount since it exists purely as a no-risk floor for a broke
// captain. Baked into the stored rewardCr at acceptance (not payout), so
// every existing completion path (Combat/Mining's own, plus Hauling/
// VIP's time-bonus multiplier in missionChecks.ts) just works unmodified
// on top of it.
const LOANER_REWARD_MULTIPLIER = 0.3;

function loanerAdjustedReward(rewardCr: number): number {
  return state.isLoanerShip ? Math.round(rewardCr * LOANER_REWARD_MULTIPLIER) : rewardCr;
}

function acceptMission(mission: BoardMission): void {
  // Poster faction (16.48/16.55) -- whichever real faction/Pirates
  // controls the System this board was generated for (see
  // generateBoard's own `getSystem(state.currentSystemId)` above); null
  // for a board with no faction poster (safe/neutral space).
  const boardSystem = getSystem(state.currentSystemId);
  const posterFaction = boardSystem ? posterFactionForSystem(boardSystem) : null;

  if (mission.type === "Combat") {
    const c = mission.combat;
    state.acceptedMissions.push({
      id: createMissionId(),
      type: "Combat",
      encounterId: c.encounter.id,
      systemId: mission.systemId,
      rewardCr: loanerAdjustedReward(c.rewardCr),
      posterFaction
    });
    state.lastEvent = `Accepted: ${c.encounter.name}.`;
  } else if (mission.type === "Mining") {
    const mm = mission.mining;
    state.acceptedMissions.push({
      id: createMissionId(),
      type: "Mining",
      systemId: mission.systemId,
      resource: mm.resource,
      quotaM3: mm.quotaM3,
      taggedM3: 0,
      hullTier: state.currentShip!.hullTier as MiningHullTier,
      rewardCr: loanerAdjustedReward(mm.rewardCr),
      posterFaction
    });
    state.lastEvent = `Accepted: mine ${mm.quotaM3} m3 ${mm.resource}.`;
  } else if (mission.type === "Hauling") {
    const h = mission.hauling;
    const parTimeSeconds = routeParTimeSeconds(h.hops, 1.0, 1.0);
    state.acceptedMissions.push({
      id: createMissionId(),
      type: "Hauling",
      destinationSystemId: h.destinationSystemId,
      rewardCr: loanerAdjustedReward(h.rewardCr),
      hullTier: h.hullTier, // sizes the sealed container's real cargo footprint (16.39)
      posterFaction,
      parTimeSeconds,
      acceptedAt: effectiveNow(state.clock, Date.now())
    });
    state.lastEvent = `Accepted: haul to ${getSystem(h.destinationSystemId)?.name ?? h.destinationSystemId}.`;
  } else {
    const v = mission.vip;
    const parTimeSeconds = routeParTimeSeconds(v.hops, 1.0, 1.0);
    const deadlineSeconds = parTimeSeconds * 1.5;
    state.acceptedMissions.push({
      id: createMissionId(),
      type: "VIP Transport",
      destinationSystemId: v.destinationSystemId,
      rewardCr: loanerAdjustedReward(v.rewardCr),
      acceptedAt: effectiveNow(state.clock, Date.now()),
      deadlineSeconds,
      posterFaction,
      parTimeSeconds
    });
    state.lastEvent = `Accepted: VIP transport to ${getSystem(v.destinationSystemId)?.name ?? v.destinationSystemId} -- deadline ${formatSeconds(deadlineSeconds)}.`;
  }
}
